import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { GRADES } from "./Signup";

const PAGE_SIZE = 10;

export default function Assessment() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState("intake");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [intake, setIntake] = useState({
    studentName: user?.name || "", grade: user?.grade || "", school: user?.school || "", higherEducation: "",
  });

  useEffect(() => {
    api.get("/api/questions", { params: { lang } }).then((r) => setQuestions(r.data)).catch(() => {});
  }, [lang]);

  const total = questions.length;
  const pages = Math.ceil(total / PAGE_SIZE);
  const pageQs = useMemo(() => questions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [questions, page]);
  const answeredCount = Object.keys(answers).length;
  const pageDone = pageQs.every((q) => answers[q.id]);

  const start = (e) => {
    e.preventDefault();
    setStage("test");
    window.scrollTo(0, 0);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/api/assessments", { ...intake, answers });
      toast.success("Done!");
      navigate(`/results/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (stage === "intake") {
    return (
      <div className="mx-auto max-w-md px-4 py-16" data-testid="assessment-intake">
        <h1 className="font-display text-3xl font-semibold sm:text-4xl" data-testid="intake-title">{t("assess.intake.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("assess.intake.sub")}</p>
        <form onSubmit={start} className="mt-8 space-y-4">
          <div>
            <Label htmlFor="sn">{t("assess.student")}</Label>
            <Input id="sn" required value={intake.studentName} onChange={(e) => setIntake({ ...intake, studentName: e.target.value })} data-testid="intake-student-name" />
          </div>
          <div>
            <Label>{t("auth.grade")}</Label>
            <Select value={intake.grade} onValueChange={(v) => setIntake({ ...intake, grade: v })}>
              <SelectTrigger data-testid="intake-grade-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="sc">{t("auth.school")}</Label>
            <Input id="sc" value={intake.school} onChange={(e) => setIntake({ ...intake, school: e.target.value })} data-testid="intake-school" />
          </div>
          <div>
            <Label>{t("assess.higherEd")}</Label>
            <Select value={intake.higherEducation} onValueChange={(v) => setIntake({ ...intake, higherEducation: v })}>
              <SelectTrigger data-testid="intake-highered-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Domestic">{t("assess.domestic")}</SelectItem>
                <SelectItem value="International">{t("assess.international")}</SelectItem>
                <SelectItem value="Not sure">{t("assess.none")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" className="w-full" data-testid="intake-start-button">{t("assess.start")}</Button>
          <p className="text-center text-xs text-muted-foreground">{t("assess.hint")}</p>
        </form>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10" data-testid="assessment-test">
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
          <span data-testid="assessment-progress-label">{t("assess.progress", { x: Math.min(answeredCount, total), n: total })}</span>
          <span>{Math.round((answeredCount / Math.max(total, 1)) * 100)}%</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${(answeredCount / Math.max(total, 1)) * 100}%` }} data-testid="assessment-progress-bar" />
        </div>
      </div>

      <div className="space-y-4">
        {pageQs.map((q) => (
          <div key={q.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`question-${q.id}`}>
            <p className="text-sm font-medium leading-relaxed">
              <span className="mr-2 text-xs text-muted-foreground">{q.order}.</span>{q.text}
            </p>
            <div className="mt-4 flex items-center gap-1.5 sm:gap-2">
              <span className="mr-1 hidden w-20 text-right text-[10px] text-muted-foreground sm:block">{t("assess.scale.1")}</span>
              {[1, 2, 3, 4, 5, 6, 7].map((v) => {
                const active = answers[q.id] === v;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setAnswers({ ...answers, [q.id]: v })}
                    className={`h-9 w-9 shrink-0 rounded-full border text-xs font-semibold transition-all sm:h-10 sm:w-10 ${
                      active ? "border-primary bg-primary text-primary-foreground scale-110" : "border-border bg-background text-muted-foreground hover:border-primary hover:text-primary"
                    }`}
                    data-testid={`answer-${q.id}-${v}`}
                    aria-label={`${v}`}
                  >
                    {v}
                  </button>
                );
              })}
              <span className="ml-1 hidden w-20 text-[10px] text-muted-foreground sm:block">{t("assess.scale.7")}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <Button variant="outline" disabled={page === 0} onClick={() => { setPage(page - 1); window.scrollTo(0, 0); }} data-testid="assessment-prev">
          <ArrowLeft className="mr-1 h-4 w-4" />{t("assess.prev")}
        </Button>
        {page < pages - 1 ? (
          <Button disabled={!pageDone} onClick={() => { setPage(page + 1); window.scrollTo(0, 0); }} data-testid="assessment-next">
            {t("assess.next")}<ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        ) : (
          <Button disabled={answeredCount < total || busy} onClick={submit} data-testid="assessment-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("assess.submit")}
          </Button>
        )}
      </div>
    </div>
  );
}
